import {useEffect, useMemo, useState} from 'react';
import {Layout} from '@/components/Layout';
import {Plus, Clock, User, MessageSquare, AlertCircle, CheckCircle2, Trash2, Save, Paperclip, FileText, Image, Video, Download} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {Badge} from '@/components/ui/badge';
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter} from '@/components/ui/dialog';
import {Label} from '@/components/ui/label';
import {Input} from '@/components/ui/input';
import {Textarea} from '@/components/ui/textarea';
import {toast} from 'sonner';
import {usePermissions} from '@/hooks/usePermissions';
import {api, getApiErrorMessage, occurrencePriorityEndpoints, occurrenceTypeEndpoints, ticketEndpoints, userEndpoints} from '@/lib/api';
import {PERMISSIONS} from '@/lib/permissions';

const TICKET_ACTION_PERMISSIONS = {
    create: PERMISSIONS.TICKET_CREATE,
    read: PERMISSIONS.TICKET_READ,
    update: PERMISSIONS.TICKET_UPDATE,
    delete: PERMISSIONS.TICKET_DELETE,
};

const emptyForm = {
    occurrence_reference: '',
    tag_code: '',
    location: '',
    type: '',
    priority: 'normal',
    status: '0',
    assigned_user_id: '',
    piket_alerted: false,
    description: '',
};

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const toApiId = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const number = Number(value);
    return Number.isInteger(number) ? number : value;
};

const unwrapAttributes = (source = {}) =>
    source.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
        ? {...source, ...source.attributes}
        : source;

const normalizeStatus = (status) => {
    const value = String(status ?? '').toLowerCase();
    const map = {
        0: 'aberto',
        1: 'em_progresso',
        2: 'resolvido',
        3: 'fechado',
        open: 'aberto',
        aberto: 'aberto',
        pending: 'aberto',
        in_progress: 'em_progresso',
        em_progresso: 'em_progresso',
        progress: 'em_progresso',
        resolved: 'resolvido',
        resolvido: 'resolvido',
        closed: 'fechado',
        fechado: 'fechado',
    };
    return map[value] || value || 'aberto';
};

const statusToApiValue = (status) => {
    const value = String(status ?? '').toLowerCase();
    const map = {
        aberto: 0,
        open: 0,
        pending: 0,
        em_progresso: 1,
        in_progress: 1,
        progress: 1,
        resolvido: 2,
        resolved: 2,
        fechado: 3,
        closed: 3,
    };
    if (['0', '1', '2', '3'].includes(value)) return Number(value);
    return map[value] ?? 0;
};

const statusToFormValue = (status) => String(statusToApiValue(status));

const normalizePriority = (priority) => {
    const value = String(priority ?? '').toLowerCase();
    const map = {
        low: 'baixa',
        baixa: 'baixa',
        normal: 'normal',
        medium: 'normal',
        high: 'alta',
        alta: 'alta',
        critical: 'critica',
        critica: 'critica',
    };
    return map[value] || value || 'normal';
};

const normalizeUser = (source = {}, index = 0) => {
    const user = unwrapAttributes(source);
    const firstName = user.first_name ?? '';
    const lastName = user.last_name ?? '';
    const derivedName = `${firstName} ${lastName}`.trim();
    return {
        id: source.id ?? user.id ?? user.user_id ?? user.company_user_id ?? index + 1,
        name: user.name ?? user.full_name ?? user.display_name ?? derivedName ?? user.email ?? `Utilizador ${index + 1}`,
        email: user.email ?? '',
    };
};

const normalizeUsers = (payload) =>
    collectionPayload(payload, 'users')
        .map(normalizeUser)
        .filter(user => user.id && user.name);

const normalizeOccurrenceType = (source = {}, index = 0) => {
    const type = unwrapAttributes(source);
    return {
        id: source.id ?? type.id ?? type.occurrence_type_id ?? type.uuid ?? index + 1,
        name: type.name ?? type.title ?? type.label ?? `Tipo ${index + 1}`,
    };
};

const normalizeOccurrenceTypes = (payload) =>
    collectionPayload(payload, 'occurrence_types')
        .map(normalizeOccurrenceType)
        .filter(type => type.id && type.name);

const normalizeOccurrencePriority = (source = {}, index = 0) => {
    const priority = unwrapAttributes(source);
    return {
        id: source.id ?? priority.id ?? priority.occurrence_priority_id ?? priority.uuid ?? index + 1,
        name: priority.name ?? priority.title ?? priority.label ?? `Prioridade ${index + 1}`,
    };
};

const normalizeOccurrencePriorities = (payload) =>
    collectionPayload(payload, 'occurrence_priorities')
        .map(normalizeOccurrencePriority)
        .filter(priority => priority.id && priority.name);

const findByIdOrName = (items, value) => {
    if (value === undefined || value === null || value === '') return null;
    const normalizedValue = String(value).trim().toLowerCase();

    return items.find(item =>
        String(item.id) === String(value)
        || String(item.name ?? '').trim().toLowerCase() === normalizedValue
    ) ?? null;
};

const isNumericLike = (value) => /^\d+$/.test(String(value ?? '').trim());

const splitDateTime = (value) => {
    if (!value) return '';
    return String(value).replace('T', ' ').slice(0, 16);
};

const normalizeOccurrenceAttachment = (source = {}, index = 0) => {
    const attachment = unwrapAttributes(source);
    const id = source.id ?? attachment.id ?? attachment.attachment_id ?? index + 1;
    const fileName = attachment.file_name ?? attachment.filename ?? attachment.name ?? attachment.attachment_name ?? '';
    const message = attachment.message ?? attachment.text ?? '';

    return {
        id,
        uuid: attachment.uuid ?? '',
        type: attachment.type ?? (message ? 'text' : 'file'),
        message,
        fileName,
        hasFile: Boolean(attachment.has_file ?? attachment.attachment ?? attachment.path ?? attachment.url ?? attachment.download_url),
        downloadUrl: attachment.download_url ?? attachment.url ?? attachment.attachment_url ?? '',
        createdAt: splitDateTime(attachment.created_at ?? attachment.createdAt),
    };
};

const normalizeOccurrenceAttachments = (...sources) => {
    for (const source of sources) {
        const attachments = collectionPayload(source, 'attachments');
        if (attachments.length > 0) {
            return attachments.map(normalizeOccurrenceAttachment).filter(attachment => attachment.id || attachment.message || attachment.fileName);
        }
    }

    return [];
};

const countFrom = (...values) => {
    for (const value of values) {
        const count = Number(value);
        if (Number.isFinite(count)) return count;
    }

    return 0;
};

const normalizeTicket = (source = {}, fallback = {}, occurrenceTypes = []) => {
    const ticket = unwrapAttributes(source);
    const occurrence = ticket.occurrence ?? ticket.incident ?? {};
    const resource = ticket.resource ?? occurrence.resource ?? ticket.vehicle ?? {};
    const tag = ticket.tag ?? occurrence.tag ?? resource.tag ?? {};
    const assignee = ticket.assignee ?? ticket.assigned_user ?? ticket.user ?? ticket.technician ?? {};
    const assigneeName = assignee.name ?? assignee.full_name ?? ticket.assignee_name ?? ticket.assigned_user_name ?? fallback.assignee ?? '';
    const id = source.id ?? ticket.id ?? fallback.id;
    const rawOccurrenceTypeId = ticket.occurrence_type_id ?? ticket.type_id ?? ticket.gest_occurrence_type_id ?? occurrence.type_id ?? fallback.occurrenceTypeId ?? null;
    const occurrenceType = findByIdOrName(occurrenceTypes, rawOccurrenceTypeId ?? ticket.type ?? ticket.occurrence_type_name ?? occurrence.type_name ?? fallback.type);
    const rawType = ticket.type ?? ticket.title ?? ticket.subject ?? ticket.occurrence_type_name ?? occurrence.type_name ?? occurrence.title ?? fallback.type ?? '';
    const type = occurrenceType?.name ?? (isNumericLike(rawType) ? fallback.type : rawType) ?? 'Ticket';
    const rawDescription = ticket.description ?? ticket.body ?? ticket.notes ?? fallback.description ?? '';
    const occurrenceAttachments = normalizeOccurrenceAttachments(occurrence, ticket.occurrence_attachments, ticket.attachments, fallback.occurrenceAttachments);
    const occurrenceAttachmentsCount = occurrenceAttachments.length || countFrom(
        ticket.occurrence_attachments_count,
        ticket.occurrenceAttachmentsCount,
        occurrence.occurrence_attachments_count,
        occurrence.attachments_count,
        fallback.comments
    );

    return {
        ...fallback,
        ...ticket,
        id,
        uuid: ticket.uuid ?? fallback.uuid,
        displayId: ticket.code ?? ticket.reference ?? ticket.ticket_no ?? (id ? `TKT-${id}` : fallback.displayId),
        occurrenceId: ticket.occurrence_id ?? ticket.incident_id ?? occurrence.id ?? fallback.occurrenceId ?? '',
        occurrenceReference: ticket.occurrence_reference ?? occurrence.code ?? occurrence.reference ?? fallback.occurrenceReference ?? '',
        occurrenceTypeId: occurrenceType?.id ?? rawOccurrenceTypeId,
        occurrencePriorityId: ticket.occurrence_priority_id ?? ticket.priority_id ?? ticket.gest_occurrence_priority_id ?? fallback.occurrencePriorityId ?? null,
        tag: tag.code ?? tag.tag_code ?? ticket.tag_code ?? ticket.tag ?? fallback.tag ?? '-',
        location: ticket.location ?? ticket.location_name ?? ticket.address ?? resource.location ?? resource.name ?? fallback.location ?? '-',
        type: type || 'Ticket',
        priority: normalizePriority(ticket.priority ?? fallback.priority),
        status: normalizeStatus(ticket.status ?? fallback.status),
        assignee: assigneeName || 'Sem responsavel',
        assignedUserId: ticket.assigned_user_id ?? ticket.assigned_to_id ?? ticket.assignee_id ?? ticket.user_id ?? ticket.technician_id ?? assignee.id ?? fallback.assignedUserId ?? null,
        piketAlerted: Boolean(ticket.piket_alerted ?? ticket.piketAlerted ?? ticket.alert_piket ?? ticket.piket_notified ?? fallback.piketAlerted ?? false),
        createdAt: splitDateTime(ticket.created_at ?? ticket.createdAt ?? fallback.createdAt),
        updatedAt: splitDateTime(ticket.updated_at ?? ticket.updatedAt ?? fallback.updatedAt),
        description: isNumericLike(rawDescription) && occurrenceType ? '' : rawDescription,
        comments: occurrenceAttachmentsCount,
        occurrenceAttachments,
    };
};

const normalizeTickets = (payload, occurrenceTypes = []) => {
    const tickets = collectionPayload(payload, 'tickets');
    return tickets.map(ticket => normalizeTicket(ticket, {}, occurrenceTypes)).filter(ticket => ticket.id || ticket.displayId);
};

const ticketPayload = (form, occurrenceTypes = []) => {
    const occurrenceTypeId = toApiId(form.type);
    const occurrenceType = occurrenceTypes.find(type => String(type.id) === String(form.type));

    return {
        occurrence_reference: form.occurrence_reference?.trim() || null,
        tag_code: form.tag_code || null,
        location: form.location || null,
        type: occurrenceType?.name ?? null,
        occurrence_type_id: occurrenceTypeId,
        priority: form.priority,
        occurrence_priority_id: toApiId(form.priority),
        status: statusToApiValue(form.status),
        assigned_user_id: toApiId(form.assigned_user_id),
        piket_alerted: Boolean(form.piket_alerted),
        description: form.description || null,
    };
};

const TicketStatusBadge = ({status}) => {
    const styles = {
        aberto: {bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Aberto'},
        em_progresso: {bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Em Progresso'},
        resolvido: {bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Resolvido'},
        fechado: {bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', label: 'Fechado'},
    };
    const style = styles[status] || styles.aberto;

    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${style.bg} ${style.text} ${style.border}`}>
      {style.label}
    </span>
    );
};

const PriorityBadge = ({priority}) => {
    const styles = {
        baixa: {bg: 'bg-slate-100', text: 'text-slate-600', label: 'Baixa'},
        normal: {bg: 'bg-blue-100', text: 'text-blue-700', label: 'Normal'},
        alta: {bg: 'bg-amber-100', text: 'text-amber-700', label: 'Alta'},
        critica: {bg: 'bg-red-100', text: 'text-red-700', label: 'Critica'},
    };
    const style = styles[priority] || styles.normal;

    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${style.bg} ${style.text}`}>
      {style.label}
    </span>
    );
};

const attachmentIcon = (type) => {
    const normalizedType = String(type ?? '').toLowerCase();
    if (normalizedType === 'photo' || normalizedType === 'image') return Image;
    if (normalizedType === 'video') return Video;
    if (normalizedType === 'text') return FileText;
    return Paperclip;
};

const attachmentLabel = (attachment, index) => {
    if (attachment.fileName) return attachment.fileName;
    if (attachment.type === 'text') return `Mensagem ${index + 1}`;
    return `Anexo ${index + 1}`;
};

const filenameFromDisposition = (disposition) => {
    const match = String(disposition ?? '').match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    return match ? decodeURIComponent(match[1].replace(/"$/, '')) : '';
};

export const TicketsPage = () => {
    const {can} = usePermissions();
    const [tickets, setTickets] = useState([]);
    const [users, setUsers] = useState([]);
    const [occurrenceTypes, setOccurrenceTypes] = useState([]);
    const [occurrencePriorities, setOccurrencePriorities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [usersLoading, setUsersLoading] = useState(true);
    const [occurrenceTypesLoading, setOccurrenceTypesLoading] = useState(true);
    const [occurrencePrioritiesLoading, setOccurrencePrioritiesLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const ticketPermissions = {
        create: can(TICKET_ACTION_PERMISSIONS.create),
        read: can(TICKET_ACTION_PERMISSIONS.read),
        update: can(TICKET_ACTION_PERMISSIONS.update),
        delete: can(TICKET_ACTION_PERMISSIONS.delete),
    };
    const canReadTickets = ticketPermissions.read;

    const loadTickets = async () => {
        if (!canReadTickets) {
            setTickets([]);
            setError('Nao tem permissão para consultar tickets.');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError('');
        try {
            const response = await api.get(ticketEndpoints.list);
            setTickets(normalizeTickets(response.data, occurrenceTypes));
        } catch (err) {
            setTickets([]);
            setError(getApiErrorMessage(err, 'Nao foi possivel carregar os tickets.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;

        const loadInitialData = async () => {
            if (!canReadTickets) {
                setTickets([]);
                setUsers([]);
                setOccurrenceTypes([]);
                setOccurrencePriorities([]);
                setError('Nao tem permissao para consultar tickets.');
                setLoading(false);
                setUsersLoading(false);
                setOccurrenceTypesLoading(false);
                setOccurrencePrioritiesLoading(false);
                return;
            }

            setLoading(true);
            setUsersLoading(true);
            setOccurrenceTypesLoading(true);
            setOccurrencePrioritiesLoading(true);
            setError('');
            try {
                const [ticketsResponse, usersResponse, occurrenceTypesResponse, occurrencePrioritiesResponse] = await Promise.all([
                    api.get(ticketEndpoints.list),
                    api.get(userEndpoints.list).catch(error => ({data: [], error})),
                    api.get(occurrenceTypeEndpoints.list).catch(error => ({data: [], error})),
                    api.get(occurrencePriorityEndpoints.list).catch(error => ({data: [], error})),
                ]);

                if (!cancelled) {
                    const normalizedOccurrenceTypes = normalizeOccurrenceTypes(occurrenceTypesResponse.data);

                    setTickets(normalizeTickets(ticketsResponse.data, normalizedOccurrenceTypes));
                    setUsers(normalizeUsers(usersResponse.data));
                    setOccurrenceTypes(normalizedOccurrenceTypes);
                    setOccurrencePriorities(normalizeOccurrencePriorities(occurrencePrioritiesResponse.data));
                    if (usersResponse.error) toast.error(getApiErrorMessage(usersResponse.error, 'Nao foi possivel carregar os utilizadores.'));
                    if (occurrenceTypesResponse.error) toast.error(getApiErrorMessage(occurrenceTypesResponse.error, 'Nao foi possivel carregar os tipos de ocorrencia.'));
                    if (occurrencePrioritiesResponse.error) toast.error(getApiErrorMessage(occurrencePrioritiesResponse.error, 'Nao foi possivel carregar os niveis de prioridade.'));
                }
            } catch (err) {
                if (!cancelled) {
                    setTickets([]);
                    setError(getApiErrorMessage(err, 'Nao foi possivel carregar os tickets.'));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setUsersLoading(false);
                    setOccurrenceTypesLoading(false);
                    setOccurrencePrioritiesLoading(false);
                }
            }
        };

        loadInitialData();

        return () => {
            cancelled = true;
        };
    }, [canReadTickets]);

    const updateForm = (key, value) => setForm(prev => ({...prev, [key]: value}));

    const ticketTypeOption = (ticket) =>
        findByIdOrName(
            occurrenceTypes,
            ticket.occurrenceTypeId ?? ticket.occurrence_type_id ?? ticket.gest_occurrence_type_id ?? ticket.type
        );

    const openCreateDialog = () => {
        if (!ticketPermissions.create) {
            toast.error('Nao tem permissao para criar tickets.');
            return;
        }

        setForm(emptyForm);
        setIsCreateDialogOpen(true);
    };

    const openTicketDialog = (ticket) => {
        const occurrenceType = ticketTypeOption(ticket);
        const description = isNumericLike(ticket.description) && occurrenceType
            ? ''
            : ticket.description;

        setSelectedTicket(ticket);
        setForm({
            occurrence_reference: ticket.occurrenceReference ? String(ticket.occurrenceReference) : '',
            tag_code: ticket.tag === '-' ? '' : ticket.tag,
            location: ticket.location === '-' ? '' : ticket.location,
            type: occurrenceType ? String(occurrenceType.id) : '',
            priority: ticket.occurrencePriorityId ? String(ticket.occurrencePriorityId) : ticket.priority,
            status: statusToFormValue(ticket.status),
            assigned_user_id: ticket.assignedUserId ? String(ticket.assignedUserId) : '',
            piket_alerted: ticket.piketAlerted,
            description,
        });
    };

    const handleCreateTicket = async () => {
        if (!ticketPermissions.create) {
            toast.error('Nao tem permissao para criar tickets.');
            return;
        }

        if (!form.type.trim()) {
            toast.error('Indique o tipo do ticket.');
            return;
        }
        setSubmitting(true);
        try {
            const response = await api.post(ticketEndpoints.create, ticketPayload(form, occurrenceTypes));
            const created = normalizeTicket(response.data?.data ?? response.data?.ticket ?? response.data, {
                id: Date.now(),
                ...form,
            }, occurrenceTypes);
            setTickets(prev => [created, ...prev]);
            setIsCreateDialogOpen(false);
            toast.success('Ticket criado.');
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Nao foi possivel criar o ticket.'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateTicket = async (patch = {}) => {
        if (!selectedTicket) return;
        if (!ticketPermissions.update) {
            toast.error('Nao tem permissao para atualizar tickets.');
            return;
        }

        const nextForm = {...form, ...patch};
        setSubmitting(true);
        try {
            const response = await api.post(ticketEndpoints.update, {
                id: selectedTicket.id,
                uuid: selectedTicket.uuid,
                ...ticketPayload(nextForm, occurrenceTypes),
            });
            const updated = normalizeTicket(response.data?.data ?? response.data?.ticket ?? response.data, {
                ...selectedTicket,
                ...nextForm,
            }, occurrenceTypes);
            setTickets(prev => prev.map(ticket => String(ticket.id) === String(selectedTicket.id) ? updated : ticket));
            setSelectedTicket(null);
            setForm(nextForm);
            toast.success('Ticket atualizado.');
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Nao foi possivel atualizar o ticket.'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteTicket = async () => {
        if (!selectedTicket) return;
        if (!ticketPermissions.delete) {
            toast.error('Nao tem permissao para remover tickets.');
            return;
        }

        setSubmitting(true);
        try {
            await api.post(ticketEndpoints.delete, {
                id: selectedTicket.id,
                uuid: selectedTicket.uuid,
            });
            setTickets(prev => prev.filter(ticket => String(ticket.id) !== String(selectedTicket.id)));
            setSelectedTicket(null);
            toast.success('Ticket removido.');
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Nao foi possivel remover o ticket.'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownloadOccurrenceAttachment = async (attachment) => {
        if (!attachment?.id) return;

        try {
            const response = await api.get(
                attachment.downloadUrl || ticketEndpoints.occurrenceAttachmentDownload(attachment.id),
                {responseType: 'blob'}
            );
            const filename = filenameFromDisposition(response.headers?.['content-disposition'])
                || attachment.fileName
                || `anexo-${attachment.id}`;
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Nao foi possivel descarregar o anexo.'));
        }
    };

    const filteredTickets = tickets.filter(ticket => {
        const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
        const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;
        return matchesStatus && matchesPriority;
    });

    const stats = useMemo(() => ({
        aberto: tickets.filter(ticket => ticket.status === 'aberto').length,
        emProgresso: tickets.filter(ticket => ticket.status === 'em_progresso').length,
        resolvido: tickets.filter(ticket => ticket.status === 'resolvido').length,
        piket: tickets.filter(ticket => ticket.piketAlerted).length,
    }), [tickets]);

    const renderTicketForm = ({disabled = false} = {}) => (
        <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="ticket-tag">Tag / Posto</Label>
                    <Input id="ticket-tag" value={form.tag_code} onChange={event => updateForm('tag_code', event.target.value)} placeholder="TAG-00432" disabled={disabled}/>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="ticket-type">Tipo de Ocorrencia</Label>
                    <Select value={form.type} onValueChange={value => updateForm('type', value)} disabled={disabled || occurrenceTypesLoading}>
                        <SelectTrigger id="ticket-type"><SelectValue placeholder="Selecionar tipo"/></SelectTrigger>
                        <SelectContent>
                            {occurrenceTypes.map(type => (
                                <SelectItem key={type.id} value={String(type.id)}>{type.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {!occurrenceTypesLoading && occurrenceTypes.length === 0 && (
                        <p className="text-xs text-slate-500">Sem tipos de ocorrencia disponiveis.</p>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="ticket-priority">Prioridade</Label>
                    <Select value={form.priority} onValueChange={value => updateForm('priority', value)} disabled={disabled || occurrencePrioritiesLoading}>
                        <SelectTrigger id="ticket-priority"><SelectValue placeholder="Selecionar prioridade"/></SelectTrigger>
                        <SelectContent>
                            {occurrencePriorities.map(priority => (
                                <SelectItem key={priority.id} value={String(priority.id)}>{priority.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {!occurrencePrioritiesLoading && occurrencePriorities.length === 0 && (
                        <p className="text-xs text-slate-500">Sem niveis de prioridade disponiveis.</p>
                    )}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="ticket-status">Estado</Label>
                    <Select value={form.status} onValueChange={value => updateForm('status', value)} disabled={disabled}>
                        <SelectTrigger id="ticket-status"><SelectValue/></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="0">Aberto</SelectItem>
                            <SelectItem value="1">Em Progresso</SelectItem>
                            <SelectItem value="2">Resolvido</SelectItem>
                            <SelectItem value="3">Fechado</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="ticket-assignee">Atribuir a</Label>
                    <Select value={form.assigned_user_id} onValueChange={value => updateForm('assigned_user_id', value)} disabled={disabled || usersLoading}>
                        <SelectTrigger id="ticket-assignee"><SelectValue placeholder="Selecionar tecnico"/></SelectTrigger>
                        <SelectContent>
                            {users.map(user => (
                                <SelectItem key={user.id} value={String(user.id)}>{user.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {usersLoading && <p className="text-xs text-slate-500">A carregar utilizadores...</p>}
                    {!usersLoading && users.length === 0 && (
                        <p className="text-xs text-slate-500">Sem utilizadores disponiveis.</p>
                    )}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="ticket-occurrence">Ocorrencia</Label>
                    <Input id="ticket-occurrence" value={form.occurrence_reference} onChange={event => updateForm('occurrence_reference', event.target.value)} placeholder="Referencia opcional" disabled={disabled}/>
                </div>
            </div>

            <div className="space-y-2">
                <Label htmlFor="ticket-location">Localizacao</Label>
                <Input id="ticket-location" value={form.location} onChange={event => updateForm('location', event.target.value)} placeholder="Localizacao do problema" disabled={disabled}/>
            </div>

            <div className="space-y-2">
                <Label htmlFor="ticket-description">Descricao</Label>
                <Textarea id="ticket-description" value={form.description} onChange={event => updateForm('description', event.target.value)} rows={4} disabled={disabled}/>
            </div>

            <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                <input type="checkbox" checked={form.piket_alerted} onChange={event => updateForm('piket_alerted', event.target.checked)} disabled={disabled}/>
                Piket alertado
            </label>
        </div>
    );

    const renderOccurrenceAttachments = () => {
        const attachments = selectedTicket?.occurrenceAttachments ?? [];

        return (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Paperclip className="text-slate-500" size={18}/>
                        <span className="font-bold text-slate-900 text-sm">Anexos da ocorrencia</span>
                    </div>
                    <Badge variant="outline" className="text-xs">{attachments.length}</Badge>
                </div>

                {attachments.length === 0 ? (
                    <p className="text-sm text-slate-500">Sem anexos associados a ocorrencia.</p>
                ) : (
                    <div className="space-y-2">
                        {attachments.map((attachment, index) => {
                            const Icon = attachmentIcon(attachment.type);
                            const label = attachmentLabel(attachment, index);

                            return (
                                <div key={attachment.uuid || attachment.id || index} className="flex items-start justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                                    <div className="flex min-w-0 items-start gap-3">
                                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-slate-600 ring-1 ring-slate-200">
                                            <Icon size={16}/>
                                        </div>
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-medium text-slate-900">{label}</p>
                                            {attachment.message && (
                                                <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-700">{attachment.message}</p>
                                            )}
                                            {attachment.createdAt && (
                                                <p className="mt-1 text-xs text-slate-500">{attachment.createdAt}</p>
                                            )}
                                        </div>
                                    </div>
                                    {attachment.hasFile && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="shrink-0"
                                            onClick={() => handleDownloadOccurrenceAttachment(attachment)}
                                        >
                                            <Download size={14} className="mr-2"/>
                                            Abrir
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Sistema de Tickets</h1>
                        <p className="text-slate-600 font-primary mt-1">Gestao de ocorrencias e assistencia tecnica</p>
                    </div>
                    {ticketPermissions.create && (
                        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                            <DialogTrigger asChild>
                                <Button data-testid="create-ticket-btn" className="bg-blue-600 hover:bg-blue-700" onClick={openCreateDialog}>
                                    <Plus size={18} className="mr-2"/>
                                    Novo Ticket
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                                <DialogHeader>
                                    <DialogTitle>Criar Novo Ticket</DialogTitle>
                                    <DialogDescription>Registar ocorrencia e criar ticket para assistencia</DialogDescription>
                                </DialogHeader>
                                {renderTicketForm()}
                                <DialogFooter>
                                    <Button variant="outline" disabled={submitting} onClick={() => setIsCreateDialogOpen(false)}>Cancelar</Button>
                                    <Button data-testid="confirm-create-ticket" disabled={submitting} className="bg-blue-600 hover:bg-blue-700" onClick={handleCreateTicket}>
                                        {submitting ? 'A guardar...' : 'Criar Ticket'}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>

                {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center justify-between">
                            <div><p className="text-slate-600 text-sm font-medium">Abertos</p><p className="text-3xl font-secondary font-bold text-amber-600 mt-1">{stats.aberto}</p></div>
                            <div className="bg-amber-50 p-3 rounded-lg"><AlertCircle className="text-amber-600" size={24}/></div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center justify-between">
                            <div><p className="text-slate-600 text-sm font-medium">Em Progresso</p><p className="text-3xl font-secondary font-bold text-blue-600 mt-1">{stats.emProgresso}</p></div>
                            <div className="bg-blue-50 p-3 rounded-lg"><Clock className="text-blue-600" size={24}/></div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center justify-between">
                            <div><p className="text-slate-600 text-sm font-medium">Resolvidos</p><p className="text-3xl font-secondary font-bold text-emerald-600 mt-1">{stats.resolvido}</p></div>
                            <div className="bg-emerald-50 p-3 rounded-lg"><CheckCircle2 className="text-emerald-600" size={24}/></div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center justify-between">
                            <div><p className="text-slate-600 text-sm font-medium">Piket Alertado</p><p className="text-3xl font-secondary font-bold text-red-600 mt-1">{stats.piket}</p></div>
                            <div className="bg-red-50 p-3 rounded-lg"><AlertCircle className="text-red-600" size={24}/></div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                    <div className="p-6 border-b border-slate-200">
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger data-testid="ticket-status-filter" className="w-48"><SelectValue placeholder="Estado"/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos Estados</SelectItem>
                                        <SelectItem value="aberto">Aberto</SelectItem>
                                        <SelectItem value="em_progresso">Em Progresso</SelectItem>
                                        <SelectItem value="resolvido">Resolvido</SelectItem>
                                        <SelectItem value="fechado">Fechado</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                                    <SelectTrigger data-testid="ticket-priority-filter" className="w-48"><SelectValue placeholder="Prioridade"/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas Prioridades</SelectItem>
                                        <SelectItem value="baixa">Baixa</SelectItem>
                                        <SelectItem value="normal">Normal</SelectItem>
                                        <SelectItem value="alta">Alta</SelectItem>
                                        <SelectItem value="critica">Critica</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button variant="outline" size="sm" onClick={loadTickets} disabled={!canReadTickets || loading}>Atualizar</Button>
                        </div>
                    </div>

                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Ticket ID</TableHead>
                                <TableHead>Tag / Localizacao</TableHead>
                                <TableHead>Tipo</TableHead>
                                <TableHead>Prioridade</TableHead>
                                <TableHead>Responsavel</TableHead>
                                <TableHead>Estado</TableHead>
                                <TableHead>Piket</TableHead>
                                <TableHead>Criado</TableHead>
                                <TableHead>Comentarios</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {!loading && filteredTickets.map((ticket) => (
                                <TableRow key={ticket.id ?? ticket.displayId} data-testid={`ticket-row-${ticket.id ?? ticket.displayId}`} className="cursor-pointer hover:bg-slate-50" onClick={() => openTicketDialog(ticket)}>
                                    <TableCell className="font-secondary font-bold text-slate-900">{ticket.displayId}</TableCell>
                                    <TableCell>
                                        <div><p className="text-slate-900 font-medium text-sm">{ticket.tag}</p><p className="text-slate-600 text-xs">{ticket.location}</p></div>
                                    </TableCell>
                                    <TableCell className="text-slate-600 text-sm">{ticket.type}</TableCell>
                                    <TableCell><PriorityBadge priority={ticket.priority}/></TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center"><User className="text-blue-600" size={13}/></div>
                                            <span className="text-sm text-slate-900">{ticket.assignee}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell><TicketStatusBadge status={ticket.status}/></TableCell>
                                    <TableCell>
                                        {ticket.piketAlerted ? (
                                            <Badge variant="destructive" className="text-xs"><AlertCircle size={12} className="mr-1"/>Alertado</Badge>
                                        ) : <span className="text-xs text-slate-400">-</span>}
                                    </TableCell>
                                    <TableCell className="text-slate-600 text-sm">{ticket.createdAt}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1 text-slate-600"><MessageSquare size={14}/><span className="text-sm">{ticket.comments}</span></div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {loading ? (
                        <div className="p-8 text-center text-sm text-slate-500">A carregar tickets...</div>
                    ) : filteredTickets.length === 0 && (
                        <div className="p-8 text-center text-sm text-slate-500">Nenhum ticket encontrado.</div>
                    )}
                </div>

                {selectedTicket && (
                    <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
                        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-3">
                                    <span>{selectedTicket.displayId}</span>
                                    <TicketStatusBadge status={selectedTicket.status}/>
                                    <PriorityBadge priority={selectedTicket.priority}/>
                                </DialogTitle>
                                <DialogDescription>{selectedTicket.type}</DialogDescription>
                            </DialogHeader>

                            {renderTicketForm({disabled: !ticketPermissions.update || submitting})}

                            {renderOccurrenceAttachments()}

                            {selectedTicket.piketAlerted && (
                                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <AlertCircle className="text-red-600" size={18}/>
                                        <span className="font-bold text-red-900 text-sm">Piket de Assistencia Alertado</span>
                                    </div>
                                    <p className="text-xs text-red-700">Chamada automatica realizada para o responsavel do piket.</p>
                                </div>
                            )}

                            <DialogFooter>
                                <Button variant="outline" onClick={() => setSelectedTicket(null)} disabled={submitting}>Cancelar</Button>
                                {ticketPermissions.delete && (
                                    <Button variant="destructive" onClick={handleDeleteTicket} disabled={submitting}>
                                        <Trash2 size={16} className="mr-2"/>Eliminar
                                    </Button>
                                )}
                                {ticketPermissions.update && (
                                    <>
                                        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => handleUpdateTicket()} disabled={submitting}>
                                            <Save size={16} className="mr-2"/>{submitting ? 'A guardar...' : 'Guardar'}
                                        </Button>
                                        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleUpdateTicket({status: '2'})} disabled={submitting}>
                                            <CheckCircle2 size={16} className="mr-2"/>Resolver
                                        </Button>
                                    </>
                                )}
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}
            </div>
        </Layout>
    );
};
